import { DatePicker, message, Pagination, Popconfirm, Table,  Modal, Tooltip  } from "antd";
import { DeleteOutlined, ReloadOutlined } from "@ant-design/icons";
import Input from "antd/es/input/Input";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    deleteCallSummarizerReport,
    getCallSummarizerReport,
    uploadCallSummarizerReport,
    checkCallSummarizerStatus,
} from "../../apis/CallSummarizerAPI";
import "../../assets/styles/pageStyle.scss";
import PageTour from "../../components/PageTour/PageTour";
import { formatDateToYYYYMMDD } from "../../libs/utilities/helpers/dateFormatter";
import { calculatePageSize } from "../../libs/utilities/helpers/paginationHelper";
import { toggleTour } from "../../reducers/tourSlice";
import UploadMedia from "./components/UploadMedia";
import { useLocation } from "react-router-dom";
import { getTitleFromPath } from "../../libs/utilities/helpers/getTitle";
import loader from '../../assets/images/loader.gif';

const { RangePicker } = DatePicker;

const CallSummarizer = () => {
    const dispatch = useDispatch();
    const location = useLocation();

    const [searchText, setSearchText] = useState("");
    const [fileList, setFileList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [fetchLoading, setFetchLoading] = useState(false);
    const [deletingId, setDeletingId] = useState(null);

    const [allReport, setAllReport] = useState([]);
    const [filteredReport, setFilteredReport] = useState([]);
    const [filteredReportWithoutSort, setFilteredReportWithoutSort] = useState([]);

    const [serviceStatusLoading, setServiceStatusLoading] = useState(true);
    const [serviceRunning, setServiceRunning] = useState(false);
    const [allserviceCompleted, setAllserviceCompleted] = useState(false);
    const [verifyReportCall, setVerifyReportCall] = useState(true);
    const { confirm } = Modal;

    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 3,
        total: 0,
    });

    const [dateRange, setDateRange] = useState({
        startDate: "",
        endDate: "",
    });

    const dateFormat = "YYYY-MM-DD";
    const defaultDateRange = [dayjs().subtract(6, "days"), dayjs()];

    const handleDelete = async (record) => {
        setDeletingId(record.document_id);
        try {
            const response = await dispatch(
                deleteCallSummarizerReport({ _id: record.document_id })
            );

            if (!response.error) {
                message.success("File deleted successfully.");
                getReport(dateRange.startDate, dateRange.endDate);
            } else {
                message.error("Failed to delete file. Please try again.");
            }
        } catch {
            message.error("An unexpected error occurred. Please try again.");
        } finally {
            setDeletingId(null);
        }
    };

    const columns = [
        {
            title: "Name",
            dataIndex: "document_name",
            key: "document_name",
            sorter: () => {},
        },
        {
            title: "Date",
            dataIndex: "createdAt",
            key: "createdAt",
        },
        {
            title: "Status",
            dataIndex: "is_processed",
            key: "is_processed",
            render: (_, record) => (
                <>
                    {record.is_processed ? (
                        <div style={{ color: "green" }}>Completed</div>
                    ) : (
                        <div style={{ color: "#1677ff" }}>In Progress</div>
                    )}
                </>
            ),
        },
   {
    title: "Action",
    key: "action",
    align: "center",
    width: 200,
    render: (_, record) => (
        <Tooltip title="Delete">
            <DeleteOutlined
                onClick={() => {
                    confirm({
                        title: "Delete file",
                        content: "Are you sure you want to delete this file?",
                        okText: "Yes",
                        cancelText: "No",
                        okButtonProps: { danger: true },
                        centered: true, // centers the modal
                        onOk: () => handleDelete(record),
                    });
                }}
                style={{
                    color: deletingId === record.document_id ? "#aaa" : "#ff4d4f",
                    fontSize: "16px",
                    cursor:
                        deletingId === record.document_id
                            ? "not-allowed"
                            : "pointer",
                    opacity:
                        deletingId === record.document_id ? 0.5 : 1,
                }}
            />
        </Tooltip>
    ),
},
    ];

    const startDate = formatDateToYYYYMMDD(dayjs().subtract(6, "days"));
    const endDate = formatDateToYYYYMMDD(dayjs());

    const verifyStatus = async () => {
        const response = await dispatch(checkCallSummarizerStatus());

        if (response.payload?.data?.running_task_ids?.length > 0) {
            setServiceRunning(true);
            setAllserviceCompleted(false);
        } else {
            setServiceRunning(false);

            if (verifyReportCall) {
                setVerifyReportCall(false);

                const formattedDates = defaultDateRange.map((dateStr) => {
                    const date = new Date(dateStr);
                    return date.toISOString().split("T")[0];
                });

                getReport(formattedDates[0], formattedDates[1]);
            }
        }

        setServiceStatusLoading(false);
    };

    useEffect(() => {
        setDateRange({ startDate, endDate });
        getReport(startDate, endDate);
    }, []);

    const handleResize = () => {
        calculatePageSize(setPagination);
    };

    const getReport = async (startDate, endDate) => {
        setFetchLoading(true);

        const response = dispatch(getCallSummarizerReport({ startDate, endDate }));
        const data = await response;

        if (!data.error) {
            setSearchText("");

            const sortedData = data.payload.data.documents.sort(
                (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
            );

            const lists = sortedData.map((item, index) => ({
                id: index,
                document_id: item._id,
                document_name: item.document_name,
                is_processed: item.is_processed,
                createdAt: formatDateToYYYYMMDD(item.createdAt),
                updatedAt: item.updatedAt,
            }));

            const allProcessed = sortedData.every((doc) => doc.is_processed === true);

            if (!allProcessed) {
                setServiceRunning(false);
                setAllserviceCompleted(false);
            } else {
                setAllserviceCompleted(true);
                setServiceRunning(true);
                setVerifyReportCall(false);
            }

            setPagination((prev) => ({ ...prev, total: lists.length }));
            setAllReport(lists);
            setFilteredReport(lists);
            setFilteredReportWithoutSort([...lists]);
            handleResize();
        }

        setFetchLoading(false);
    };

    const handleDateFilter = (dates, dateStrings) => {
        if (!dates) return;
        const startDate = formatDateToYYYYMMDD(dateStrings[0]);
        const endDate = formatDateToYYYYMMDD(dateStrings[1]);
        setDateRange({ startDate, endDate });
        getReport(startDate, endDate);
    };

    const [tableSorter, setTableSorter] = useState(null);

    const sortTableData = (_, __, sorter) => {
        setTableSorter(sorter);

        if (sorter.columnKey === "document_name") {
            if (!sorter.order) {
                setFilteredReport(filteredReportWithoutSort);
                return;
            }

            const filtered = filteredReport.sort((a, b) =>
                sorter.order === "ascend"
                    ? a.document_name.localeCompare(b.document_name)
                    : b.document_name.localeCompare(a.document_name)
            );

            setFilteredReport([...filtered]);
        }
    };

    const handlePageChange = (page, pageSize) => {
        setPagination({ ...pagination, current: page, pageSize });
    };

    const startIdx = (pagination.current - 1) * pagination.pageSize;
    const endIdx = startIdx + pagination.pageSize;
    const displayData = filteredReport.slice(startIdx, endIdx);

    const getEndOfDay = (date) => {
        const endOfDay = new Date(date);
        endOfDay.setHours(23, 59, 59, 999);
        return endOfDay;
    };

    const disabledDate = (current) => {
        return current && current > getEndOfDay(new Date());
    };

    const hasInvalidChars = (value) => /[<>/"'`;()&*#$%!^?]/.test(value);

    const handleSearch = (e) => {
        const searchString = e.target.value;

        if (hasInvalidChars(searchString)) {
            message.destroy();
            message.error("Special characters are not allowed");
            return;
        }

        setSearchText(searchString);

        const filtered = allReport.filter((item) =>
            item.document_name.toLowerCase().includes(searchString.trim().toLowerCase())
        );

        setPagination({ ...pagination, current: 1, total: filtered.length });
        setFilteredReport(filtered);
        setFilteredReportWithoutSort([...filtered]);
    };

    const handleFileSelect = (file) => {
        setFileList(file);
    };

    const generateReport = async () => {
        if (fileList.length === 0) {
            message.warning("Please upload a file");
            return;
        }

        setLoading(true);

        try {
            const data = { file: fileList[0] };
            const response = await dispatch(uploadCallSummarizerReport(data));

            if (!response.error) {
                message.success("File uploaded successfully. It will appear after processing.");
                setFileList([]);
                getReport(dateRange.startDate, dateRange.endDate);
            }
        } finally {
            setLoading(false);
        }
    };

    const tourState = useSelector((state) => state.tour);

    const handleTourClose = () => {
        dispatch(toggleTour({ show: false }));
    };

    const handleRefresh = () => {
        getReport(dateRange.startDate, dateRange.endDate);
    };

    return (
        <>
            {loading && (
                <div
                    style={{
                        position: "fixed",
                        inset: 0,
                        zIndex: 9999,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        backgroundColor: "rgba(255, 255, 255, 0.35)",
                        backdropFilter: "blur(4px)",
                        WebkitBackdropFilter: "blur(6px)",
                        pointerEvents: "all",
                    }}
                >
                    <div
                        style={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: "12px",
                            padding: "28px 36px",
                            borderRadius: "16px",
                            background: "rgba(255, 255, 255, 0.6)",
                            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
                            border: "1px solid rgba(255, 255, 255, 0.8)",
                        }}
                    >
                        <img
                            className="gif-loader"
                            src={loader}
                            alt="Loading..."
                            style={{ width: "56px", height: "56px" }}
                        />
                        <span
                            style={{
                                fontSize: "14px",
                                fontWeight: 500,
                                color: "#555",
                                letterSpacing: "0.3px",
                            }}
                        >
                            Uploading file...
                        </span>
                    </div>
                </div>
            )}

            <div className="heading-div">
                <p className="text5">{getTitleFromPath(location?.pathname)}</p>
            </div>

            <div className="rca-container">
                <PageTour
                    show={tourState.show}
                    handleShow={handleTourClose}
                    target="ant-upload-drag"
                    title="Start uploading files to generate analyser report"
                />

                <div className="file-table">
                    <div className="table-actions">
                        <Input
                            size="middle"
                            value={searchText}
                            onChange={handleSearch}
                            placeholder="Search"
                            style={{ width: 250 }}
                        />

                        <div
                            style={{
                                display: "flex",
                                gap: "10px",
                                marginLeft: "auto",
                                alignItems: "center",
                            }}
                        >
                            <RangePicker
                                className="file-datepicker"
                                onChange={handleDateFilter}
                                disabledDate={disabledDate}
                                defaultValue={defaultDateRange}
                                format={dateFormat}
                                allowClear={false}
                            />

                            <Tooltip title="Refresh">
                                <button className="btn-outlined" onClick={handleRefresh}>
                                    <div className="btn-outlined-content">
                                        <div className="text">
                                            <ReloadOutlined />
                                        </div>
                                    </div>
                                </button>
                            </Tooltip>
                        </div>
                    </div>

                    <Table
                        columns={columns}
                        dataSource={displayData}
                        loading={fetchLoading}
                        pagination={false}
                        rowKey={(record) => record.id}
                        onChange={sortTableData}
                    />

                    <Pagination
                        current={pagination.current}
                        pageSize={pagination.pageSize}
                        total={+pagination.total}
                        onChange={handlePageChange}
                        style={{ marginTop: "5px", float: "right" }}
                    />
                </div>

                <UploadMedia
                    handleFileSelect={handleFileSelect}
                    fileList={fileList}
                    handelClick={generateReport}
                    disabled={loading}
                />
            </div>
        </>
    );
};

export default CallSummarizer;